import { createLink } from '@tanstack/react-router';
import { Tooltip, TooltipTrigger } from '@vizit/atoms';
import { cva, cx } from '@vizit/theme';
import * as React from 'react';
import { Link, type LinkProps } from 'react-aria-components';
import type { VerticalNavbarItemConfig } from './types';

const styles = {
  link: cva({
    base: [
      'group flex items-center justify-center',
      'w-20 h-20',
      'bg-white hover:bg-secondary',
      'transition-colors duration-200',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-2 focus-visible:ring-offset-white',
    ],
    variants: {
      isActive: {
        true: 'bg-secondary',
      },
    },
  }),
  icon: cva({
    base: ['[&>svg]:w-12 [&>svg]:h-12', 'text-quaternary group-hover:text-secondary-hover'],
    variants: {
      isActive: {
        true: 'text-secondary',
      },
    },
  }),
};

interface VerticalNavbarItemLinkProps extends Omit<LinkProps, 'children'> {
  isActive?: boolean;
  icon: React.ReactNode;
  label: string;
}

const VerticalNavbarItemLink = React.forwardRef<HTMLAnchorElement, VerticalNavbarItemLinkProps>((props, ref) => {
  const { isActive = false, icon, label, className, ...other } = props;

  return (
    <TooltipTrigger delay={300}>
      <Link
        {...other}
        ref={ref}
        aria-current={isActive ? 'page' : undefined}
        aria-label={label}
        className={cx(styles.link({ isActive }), className)}
      >
        <span className={styles.icon({ isActive })}>{icon}</span>
      </Link>
      <Tooltip placement="right">{label}</Tooltip>
    </TooltipTrigger>
  );
});

VerticalNavbarItemLink.displayName = 'VerticalNavbarItemLink';

const RouterVerticalNavbarItemLink = createLink(VerticalNavbarItemLink);

export interface VerticalNavbarItemProps extends Omit<VerticalNavbarItemConfig, 'key'> {
  className?: string;
  /** Test ID for E2E tests */
  'data-testid'?: string;
}

function VerticalNavbarItem(props: VerticalNavbarItemProps) {
  const { to, isActive = false, icon, label, className, 'data-testid': testId } = props;

  return (
    <RouterVerticalNavbarItemLink
      to={to}
      preload="intent"
      isActive={isActive}
      icon={icon}
      label={label}
      className={className}
      data-testid={testId}
    />
  );
}

export { VerticalNavbarItem, VerticalNavbarItemLink };
